// client/src/components/common/ServiceAreaSelector.js
import React from 'react';

const SERVICE_AREAS = {
  crete: {
    label: 'Κρήτη (Crete)',
    areas: [
      { value: 'heraklion', label: 'Ηράκλειο (Heraklion)' },
      { value: 'chania', label: 'Χανιά (Chania)' },
      { value: 'rethymno', label: 'Ρέθυμνο (Rethymno)' },
      { value: 'lasithi', label: 'Λασίθι (Lasithi)' }
    ]
  },
  attica: {
    label: 'Αττική (Attica)',
    areas: [
      { value: 'athens_central', label: 'Κεντρική Αθήνα (Central Athens)' },
      { value: 'athens_north', label: 'Βόρεια Αθήνα (North Athens)' },
      { value: 'athens_south', label: 'Νότια Αθήνα (South Athens)' },
      { value: 'athens_east', label: 'Ανατολική Αθήνα (East Athens)' },
      { value: 'athens_west', label: 'Δυτική Αθήνα (West Athens)' },
      { value: 'piraeus', label: 'Πειραιάς (Piraeus)' },
      { value: 'east_attica', label: 'Ανατολική Αττική (East Attica)' },
      { value: 'west_attica', label: 'Δυτική Αττική (West Attica)' }
    ]
  },
  central: {
    label: 'Κεντρική Ελλάδα (Central Greece)',
    areas: [
      { value: 'thessaloniki', label: 'Θεσσαλονίκη (Thessaloniki)' },
      { value: 'central_macedonia', label: 'Κεντρική Μακεδονία (Central Macedonia)' },
      { value: 'larissa', label: 'Λάρισα (Larissa)' },
      { value: 'volos', label: 'Βόλος (Volos)' }
    ]
  },
  peloponnese: {
    label: 'Πελοπόννησος (Peloponnese)',
    areas: [
      { value: 'patras', label: 'Πάτρα (Patras)' },
      { value: 'kalamata', label: 'Καλαμάτα (Kalamata)' },
      { value: 'tripoli', label: 'Τρίπολη (Tripoli)' },
      { value: 'corinth', label: 'Κόρινθος (Corinth)' }
    ]
  },
  islands: {
    label: 'Νησιά (Islands)',
    areas: [
      { value: 'rhodes', label: 'Ρόδος (Rhodes)' },
      { value: 'corfu', label: 'Κέρκυρα (Corfu)' },
      { value: 'mykonos', label: 'Μύκονος (Mykonos)' },
      { value: 'santorini', label: 'Σαντορίνη (Santorini)' },
      { value: 'zakynthos', label: 'Ζάκυνθος (Zakynthos)' },
      { value: 'cephalonia', label: 'Κεφαλονιά (Cephalonia)' },
      { value: 'lesbos', label: 'Λέσβος (Lesbos)' },
      { value: 'chios', label: 'Χίος (Chios)' },
      { value: 'samos', label: 'Σάμος (Samos)' }
    ]
  },
  northern: {
    label: 'Βόρεια Ελλάδα (Northern Greece)',
    areas: [
      { value: 'ioannina', label: 'Ιωάννινα (Ioannina)' },
      { value: 'kavala', label: 'Καβάλα (Kavala)' },
      { value: 'alexandroupoli', label: 'Αλεξανδρούπολη (Alexandroupoli)' },
      { value: 'drama', label: 'Δράμα (Drama)' },
      { value: 'serres', label: 'Σέρρες (Serres)' }
    ]
  },
  special: {
    label: 'Ειδικές Επιλογές (Special Options)',
    areas: [
      { value: 'all_crete', label: 'Ολόκληρη η Κρήτη (All Crete)' },
      { value: 'all_attica', label: 'Ολόκληρη η Αττική (All Attica)' },
      { value: 'nationwide', label: 'Πανελλαδικά (Nationwide)' }
    ]
  }
};

/**
 * ServiceAreaSelector Component
 * 
 * Props:
 * - mode: 'single' | 'multiple' - Single select for business, multiple for wholesaler
 * - value: string | string[] - Current selected value(s)
 * - onChange: function - Callback when selection changes
 * - label: string - Label for the selector
 * - required: boolean - Whether field is required
 * - showSpecialOptions: boolean - Show special options like "Nationwide"
 */
const ServiceAreaSelector = ({ 
  mode = 'single', 
  value, 
  onChange, 
  label = 'Service Area',
  required = false,
  showSpecialOptions = false
}) => {
  
  const handleChange = (e) => {
    if (mode === 'single') {
      onChange(e.target.value);
    } else {
      // Multiple select mode
      const options = Array.from(e.target.selectedOptions, option => option.value);
      onChange(options);
    }
  };

  const handleCheckboxChange = (areaValue) => {
    if (mode === 'multiple') {
      const currentValues = Array.isArray(value) ? value : [];
      const newValues = currentValues.includes(areaValue)
        ? currentValues.filter(v => v !== areaValue)
        : [...currentValues, areaValue];
      onChange(newValues);
    }
  };

  // For single select mode - dropdown
  if (mode === 'single') {
    return (
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
        <select
          value={value || ''}
          onChange={handleChange}
          required={required}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          <option value="">-- Select Area --</option>
          {Object.entries(SERVICE_AREAS).map(([regionKey, region]) => {
            // Skip special options for businesses
            if (regionKey === 'special' && !showSpecialOptions) return null;
            
            return (
              <optgroup key={regionKey} label={region.label}>
                {region.areas.map(area => (
                  <option key={area.value} value={area.value}>
                    {area.label}
                  </option>
                ))}
              </optgroup>
            );
          })}
        </select>
        <p className="mt-1 text-xs text-gray-500">
          Select the area where your business is located
        </p>
      </div>
    );
  }

  // For multiple select mode - checkboxes
  const selectedAreas = Array.isArray(value) ? value : [];

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <div className="border border-gray-300 rounded-lg p-4 max-h-96 overflow-y-auto bg-white">
        {Object.entries(SERVICE_AREAS).map(([regionKey, region]) => {
          // Show special options only if enabled
          if (regionKey === 'special' && !showSpecialOptions) return null;
          
          return (
            <div key={regionKey} className="mb-4 last:mb-0">
              <h4 className="font-semibold text-gray-900 mb-2 text-sm">
                {region.label}
              </h4>
              <div className="space-y-2 ml-4">
                {region.areas.map(area => (
                  <label 
                    key={area.value} 
                    className="flex items-center cursor-pointer hover:bg-gray-50 p-2 rounded"
                  >
                    <input
                      type="checkbox"
                      checked={selectedAreas.includes(area.value)}
                      onChange={() => handleCheckboxChange(area.value)}
                      className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <span className="ml-3 text-sm text-gray-700">
                      {area.label}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          );
        })}
      </div>
      <p className="mt-2 text-xs text-gray-500">
        {selectedAreas.length === 0 
          ? 'Select the areas you deliver to'
          : `${selectedAreas.length} area${selectedAreas.length !== 1 ? 's' : ''} selected`
        }
      </p>
    </div>
  );
};

export default ServiceAreaSelector;